import React, { useState, useEffect } from 'react';
import Spinner from '../../components/common/Spinner';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import './GigDetails.css';

/**
 * GigDetails component for viewing a specific gig
 */
const GigDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gig, setGig] = useState(null);
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchGigDetails = async () => {
      try {
        setLoading(true);
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        
        // Fetch the gig details
        const { data: gigData, error: gigError } = await supabase
          .from('gigs')
          .select('*')
          .eq('id', id)
          .single();
          
        if (gigError) throw gigError;
        setGig(gigData);
        

        
        // Fetch venue details
        if (gigData.venue_id) {
          console.log('Fetching venue details for venue_id:', gigData.venue_id);
          const { data: venueData, error: venueError } = await supabase
            .from('profiles')
            .select('*')
            .eq('uuid', gigData.venue_id)
            .single();
            
          if (venueError) {
            console.error('Error fetching venue details:', venueError);
            throw venueError;
          }
          console.log('Venue data:', venueData);
          setVenue(venueData);
        }
        
        // User data is fetched for future functionality
      } catch (error) {
        console.error('Error fetching gig details:', error.message);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGigDetails();
  }, [id]);

  // Application functionality has been removed

  if (loading) {
    return (
      <div className="gig-details-container">
        <div className="gig-details-card">
          <div className="gig-body" style={{ padding: '3rem' }}>
            <Spinner message="Loading gig details..." />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gig-details-container">
        <div className="gig-details-card">
          <div className="gig-body">
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
            <button 
              className="action-button secondary-button" 
              onClick={() => navigate('/gigs')}
            >
              <i className="bi bi-arrow-left"></i> Back to Gigs
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!gig) {
    return (
      <div className="gig-details-container">
        <div className="gig-details-card">
          <div className="gig-body">
            <div className="alert alert-warning">
              <i className="bi bi-question-circle-fill me-2"></i>
              Gig not found
            </div>
            <button 
              className="action-button secondary-button" 
              onClick={() => navigate('/gigs')}
            >
              <i className="bi bi-arrow-left"></i> Back to Gigs
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gig-details-container">
      <div className="gig-details-card">
        <div className="gig-header">
          <div className="gig-title-row">
            <h1>{gig.title}</h1>
          </div>
          <p className="gig-date">{new Date(gig.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          <div className="gig-subtitle">
            <i className="bi bi-calendar-event"></i>
            {new Date(gig.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            {gig.time && (
              <>
                <i className="bi bi-clock"></i>
                {gig.time}
              </>
            )}
            {gig.is_recurring && (
              <span className="gig-recurring-badge">
                <i className="bi bi-arrow-repeat"></i> Recurring
              </span>
            )}
          </div>
          <div className="gig-actions">
            {user && venue && user.id === venue.uuid && (
              <>
                <button className="edit-gig-btn" onClick={() => navigate(`/gigs/edit/${id}`)}>
                  <i className="bi bi-pencil-square"></i> Edit Gig
                </button>
              </>
            )}
          </div>
        </div>
        
        <div className="gig-body">
          <div className="row">
            <div className="col-lg-8">
              <div className="gig-section">
                <h2 className="gig-section-title">
                  <i className="bi bi-info-circle"></i> Description
                </h2>
                <p className="gig-description">{gig.description || 'No description provided.'}</p>
              </div>
              
              <div className="gig-section">
                <h2 className="gig-section-title">
                  <i className="bi bi-list-check"></i> Requirements
                </h2>
                <div className="gig-requirements">
                  {gig.instruments && (
                    <div className="requirement-item">
                      <i className="bi bi-music-note-beamed"></i>
                      <div>
                        <div className="requirement-label">Instruments</div>
                        <div className="requirement-value">{gig.instruments}</div>
                      </div>
                    </div>
                  )}
                  
                  <div className="requirement-item">
                    <i className="bi bi-star"></i>
                    <div>
                      <div className="requirement-label">Experience Level</div>
                      <div className="requirement-value">{gig.experience_level || 'Any'}</div>
                    </div>
                  </div>
                  
                  <div className="requirement-item">
                    <i className="bi bi-hourglass-split"></i>
                    <div>
                      <div className="requirement-label">Duration</div>
                      <div className="requirement-value">{gig.duration || 'Not specified'}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="gig-section">
                <h2 className="gig-section-title">
                  <i className="bi bi-geo-alt"></i> Location
                </h2>
                <div className="location-badge">
                  <i className="bi bi-pin-map-fill"></i>
                  {gig.location || 'Location not specified'}
                </div>
              </div>
              
              <div className="gig-section">
                <h2 className="gig-section-title">
                  <i className="bi bi-cash-coin"></i> Payment
                </h2>
                <div className="payment-badge">
                  <i className="bi bi-currency-dollar"></i>
                  ${gig.pay} {gig.payment_type || 'Flat rate'}
                </div>
              </div>
            </div>
            
            <div className="col-lg-4">
              <div className="venue-card">
                <h3 className="venue-card-title">
                  <i className="bi bi-building"></i> Venue Information
                </h3>
                
                {venue ? (
                  <div>
                    <div className="venue-info-item">
                      <i className="bi bi-person-circle"></i>
                      <span><strong>Name:</strong> {venue.full_name || venue.venue_name || 'Not specified'}</span>
                    </div>
                    
                    {venue.venue_type && (
                      <div className="venue-info-item">
                        <i className="bi bi-building"></i>
                        <span><strong>Type:</strong> {venue.venue_type}</span>
                      </div>
                    )}
                    
                    {venue.contact_email && (
                      <div className="venue-info-item">
                        <i className="bi bi-envelope"></i>
                        <span><strong>Contact:</strong> {venue.contact_email}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p>Venue information not available</p>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="gig-footer">
          <button 
            className="action-button secondary-button" 
            onClick={() => navigate('/gigs')}
          >
            <i className="bi bi-arrow-left"></i> Back to Gigs
          </button>
        </div>
      </div>
    </div>
  );
};

export default GigDetails;
